import * as React from "react";
import { Query, Mutation } from "react-apollo";
import { Form, Label, FormGroup, Input, Button } from "reactstrap";
import Select from "react-select";

// GraphQL
import UpdateAssetMutation from "../../GraphQL/Mutations/UpdateAsset";
import GetCompanyAssets from "../../GraphQL/Queries/GetCompanyAssets";
import GetCompanyUsers from "../../GraphQL/Queries/GetCompanyUsers";
import GetCompanySites from "../../GraphQL/Queries/GetCompanySites";

// Presentation/UI
import Loader from "../../Components/Loader";

// Theme
import { Colors } from "../../Themes";

interface IProps {
  companyId: any;
  asset: any;
  closeModal(): void;
  notification(message: string): void;
}

interface IState {
  id: string;
  name: string;
  description: string;
  category: string;
  model: string;
  barcode: string;
  loading: boolean;
  site: any;
  user: any;
  siteId: any;
  userId: any;
  error: null | string;
}

class EditAssetForm extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      id: props.asset.id,
      name: props.asset.name,
      description: props.asset.description,
      category: props.asset.category,
      model: props.asset.model,
      barcode: props.asset.barcode,
      site: props.asset.site && {
        id: props.asset.site.id,
        value: props.asset.site,
        label: props.asset.site.name
      },
      user: props.asset.primaryUser && {
        id: props.asset.primaryUser.id,
        value: props.asset.primaryUser,
        label: `${props.asset.primaryUser.firstName} ${
          props.asset.primaryUser.lastName
        }`
      },
      loading: false,
      siteId: !props.asset.site ? null : props.asset.site.id,
      userId: !props.asset.primaryUser ? null : props.asset.primaryUser.id,
      error: null
    };
  }

  // Handle change
  handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    const { name, value }: { name: string; value: string } = e.currentTarget;

    switch (name) {
      case "partName":
        this.setState({ name: value });
        break;
      case "description":
        this.setState({ description: value });
        break;
      case "category":
        this.setState({ category: value });
        break;
      case "model":
        this.setState({ model: value });
        break;
      case "barcode":
        this.setState({ barcode: value });
        break;
      default:
        break;
    }
  };

  // Select site
  selectSite = (site: any) => {
    const siteId = site.value.id;
    this.setState({ siteId, site });
  };

  // Return sites
  returnSites = (
    sites: Array<any>
  ): Array<{ value: object; label: string }> => {
    return sites.map((site: any) => {
      return {
        value: site,
        label: `${site.name}`
      };
    });
  };

  // Select users
  selectUser = (user: any) => {
    const userId = user.value.id;
    this.setState({ userId, user });
  };

  // Return users
  returnUsers = (
    users: Array<any>
  ): Array<{ value: object; label: string }> => {
    return users.map((user: any) => {
      return {
        value: user,
        label: `${user.firstName} ${user.lastName}`
      };
    });
  };

  validateForm = (): boolean => {
    // Check for undefined or empty infput fields
    if (!this.state.name) {
      this.setError("Please fill in the name of the asset");
      return false;
    }
    return true;
  };

  // Error
  setError = (error: string): void => {
    this.setState(
      {
        error
      },
      () => {
        setTimeout(() => {
          this.setState({ error: null });
        }, 3000);
      }
    );
  };

  render() {
    const {
      id,
      name,
      description,
      category,
      model,
      barcode,
      site,
      user,
      siteId,
      userId
    } = this.state;
    const { companyId, closeModal, notification } = this.props;

    return (
      <Query query={GetCompanySites} variables={{ id: companyId }}>
        {({ loading, error, data }) => {
          if (loading) {
            return <Loader />;
          }
          if (error) {
            return <div>There was a problem loading the data</div>;
          }

          const sites = data.allSites;

          return (
            <Query query={GetCompanyUsers} variables={{ id: companyId }}>
              {usersQuery => {
                if (usersQuery.loading) {
                  return <Loader />;
                }
                if (usersQuery.error) {
                  return <div>There was a problem loading the data</div>;
                }

                const users = usersQuery.data.allUsers;

                return (
                  <Mutation<{}, {}> mutation={UpdateAssetMutation}>
                    {updateSite => (
                      <Form
                        onSubmit={e => {
                          e.preventDefault();
                          if (this.validateForm()) {
                            this.setState({ loading: true });

                            updateSite({
                              variables: {
                                id,
                                name,
                                description,
                                category,
                                model,
                                barcode,
                                siteId,
                                userId
                              },
                              refetchQueries: [
                                {
                                  query: GetCompanyAssets,
                                  variables: { id: companyId }
                                }
                              ]
                            })
                              .then(res => {
                                this.setState({ loading: false });

                                closeModal();
                                notification("Asset updated successfully");
                              })
                              .catch(err => {
                                this.setState({ loading: false });
                              });
                          }
                        }}
                      >
                        <FormGroup>
                          <Label for="assetName">Asset Name</Label>
                          <Input
                            type="text"
                            name="assetName"
                            id="assetName"
                            value={name}
                            placeholder="Asset name"
                            onChange={this.handleChange}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="description">Description</Label>
                          <Input
                            type="textarea"
                            name="description"
                            id="description"
                            value={description}
                            placeholder="Description"
                            onChange={this.handleChange}
                            style={{ resize: "none" }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="model">Model</Label>
                          <Input
                            type="text"
                            name="model"
                            id="model"
                            value={model}
                            placeholder="Model"
                            onChange={this.handleChange}
                            style={{ resize: "none" }}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="category">Category</Label>
                          <Input
                            type="text"
                            name="category"
                            id="category"
                            value={category}
                            placeholder="Category"
                            onChange={this.handleChange}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label for="barcode">Barcode</Label>
                          <Input
                            type="text"
                            name="barcode"
                            id="barcode"
                            value={barcode}
                            placeholder="Barcode"
                            onChange={this.handleChange}
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Site</Label>
                          <Select
                            onChange={this.selectSite}
                            options={this.returnSites(sites)}
                            value={site}
                            isSearchable={true}
                            placeholder="Sites"
                            className="select-styling"
                          />
                        </FormGroup>
                        <FormGroup>
                          <Label>Primary User</Label>
                          <Select
                            onChange={this.selectUser}
                            options={this.returnUsers(users)}
                            value={user}
                            isSearchable={true}
                            placeholder="Users"
                            className="select-styling"
                          />
                        </FormGroup>
                        <br />
                        <FormGroup>
                          <Button>
                            {!this.state.loading ? (
                              <span>Update</span>
                            ) : (
                              <Loader />
                            )}
                          </Button>
                        </FormGroup>
                        {this.state.error && (
                          <p
                            style={{ textAlign: "center", color: Colors.error }}
                          >
                            {this.state.error}
                          </p>
                        )}
                      </Form>
                    )}
                  </Mutation>
                );
              }}
            </Query>
          );
        }}
      </Query>
    );
  }
}

export default EditAssetForm;



// WEBPACK FOOTER //
// ./src/Containers/Assets/EditAssetForm.tsx